import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Download, 
  QrCode, 
  CheckCircle2, 
  ChevronRight,
  Package,
  ArrowLeft,
  ArrowRightLeft,
  LogOut,
  Search
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

function SafetyQRGeneration({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedPoId = searchParams.get('po_id');
  
  // Main state
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Permissions state
  const [permissions, setPermissions] = useState([]);
  
  // Step 1: PO Selection state
  const [pos, setPos] = useState([]);
  const [loadingPos, setLoadingPos] = useState(true);
  const [selectedPo, setSelectedPo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [batchNumber, setBatchNumber] = useState("01");
  const [qrDate, setQrDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  
  // Step 2: Products state
  const [products, setProducts] = useState([]);
  
  // Step 3: Generated QR codes
  const [generatedBatch, setGeneratedBatch] = useState(null);
  
  // Settings
  const [settings, setSettings] = useState({
    base_url: "https://hi.faultreport.in/?code="
  });

  const hasPermission = (perm) => permissions.includes(perm);

  // Fetch POs and settings on mount
  useEffect(() => {
    fetchPermissions();
    fetchPos();
    fetchSettings();
  }, []);

  // Handle pre-selected PO from URL
  useEffect(() => {
    if (preSelectedPoId && pos.length > 0) {
      const po = pos.find(p => p.id === preSelectedPoId);
      if (po) {
        handleSelectPo(po);
      }
    }
  }, [preSelectedPoId, pos]);

  const fetchPermissions = async () => {
    try {
      const res = await axios.get(`${API}/auth/me/permissions`, getAuthHeaders());
      setPermissions(res.data.effective_permissions || []);
    } catch (error) {
      console.error("Failed to load permissions:", error);
    }
  };

  const fetchPos = async () => {
    try {
      const response = await axios.get(`${API}/po/list`, getAuthHeaders());
      setPos(response.data);
    } catch (error) {
      console.error("Failed to load POs:", error);
      toast.error("Failed to load Purchase Orders");
    } finally {
      setLoadingPos(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/qr/settings`, getAuthHeaders());
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to load settings:", error);
    }
  };

  // Handle PO selection
  const handleSelectPo = async (po) => {
    setSelectedPo(po);
    setLoading(true);
    
    try {
      // Fetch full PO details with products
      const response = await axios.get(`${API}/po/${po.id}`, getAuthHeaders());
      const fullPo = response.data;
      
      // Set products with selection state
      setProducts(fullPo.products.map(p => ({
        ...p,
        selected: true
      })));
      
      // Set default date to today
      setQrDate(new Date().toISOString().split('T')[0]);
      
    } catch (error) {
      toast.error("Failed to load PO details");
      setSelectedPo(null);
    } finally {
      setLoading(false);
    }
  };

  // Toggle product selection
  const toggleProduct = (index) => {
    const newProducts = [...products];
    newProducts[index].selected = !newProducts[index].selected;
    setProducts(newProducts);
  };

  const selectAll = () => {
    setProducts(products.map(p => ({ ...p, selected: true })));
  };

  const deselectAll = () => {
    setProducts(products.map(p => ({ ...p, selected: false })));
  };

  // Generate QR codes
  const handleGenerateQR = async () => {
    const selectedProducts = products.filter(p => p.selected);
    
    if (selectedProducts.length === 0) {
      toast.error("Please select at least one product");
      return;
    }
    
    if (!batchNumber.trim()) {
      toast.error("Please enter a batch number");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/qr/generate-batch`, {
        po_number: selectedPo.po_number,
        batch_number: batchNumber,
        qr_date: qrDate,
        products: selectedProducts
      }, getAuthHeaders());
      
      setGeneratedBatch(response.data);
      setStep(3);
      toast.success(`Generated ${response.data.total_codes} QR codes!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate QR codes");
    } finally {
      setLoading(false);
    }
  };

  // Download ZIP
  const downloadZip = async (batchId, poNumber, batchNum, qrDateStr) => {
    try {
      const response = await axios.get(`${API}/qr/batches/${batchId}/export`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PO_${poNumber}_Batch${batchNum}_${qrDateStr}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("ZIP downloaded successfully");
    } catch (error) {
      toast.error("Failed to download ZIP");
    }
  };

  // Reset wizard
  const resetWizard = () => {
    setStep(1);
    setSelectedPo(null);
    setProducts([]);
    setGeneratedBatch(null);
    setBatchNumber("01");
    setQrDate(new Date().toISOString().split('T')[0]);
    // Clear URL params
    navigate('/safety/qr', { replace: true });
  };

  // Filter POs based on search
  const filteredPos = pos.filter(po => 
    po.po_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (po.vendor_name && po.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Status badge
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      pending: { color: "bg-gray-100 text-gray-700", label: "Pending" },
      partial: { color: "bg-yellow-100 text-yellow-700", label: "Partial" },
      complete: { color: "bg-green-100 text-green-700", label: "Complete" }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div data-testid="safety-qr-generation">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/safety")}
              variant="ghost"
              className="text-white hover:bg-white/10 -ml-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <QrCode className="w-7 h-7" />
            <div>
              <h1 className="text-xl font-bold">Generate QR Codes</h1>
              <p className="text-emerald-100 text-sm">Select PO and generate QR codes</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasPermission("module.project") && (
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Project Module
              </Button>
            )}
            <UserMenu user={user} onLogout={onLogout} variant="header" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Progress Steps */}
        <div className="bg-white rounded-lg border shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-emerald-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-emerald-600 text-white' : 'bg-gray-200'}`}>
                {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
              </div>
              <span className="font-medium hidden sm:inline">Select PO</span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-emerald-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-emerald-600 text-white' : 'bg-gray-200'}`}>
                {step > 2 ? <CheckCircle2 className="w-5 h-5" /> : '2'}
              </div>
              <span className="font-medium hidden sm:inline">Review & Configure</span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
            <div className={`flex items-center gap-2 ${step >= 3 ? 'text-emerald-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-emerald-600 text-white' : 'bg-gray-200'}`}>
                3
              </div>
              <span className="font-medium hidden sm:inline">Export</span>
            </div>
          </div>
        </div>

        {/* Step 1: Select PO */}
        {step === 1 && (
          <div className="bg-white rounded-lg border shadow-sm p-6" data-testid="step-1-select-po">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Select Purchase Order
            </h2>

            {/* Search */}
            <div className="mb-4 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by PO number or vendor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="po-search-input"
              />
            </div>

            {/* PO List */}
            {loadingPos ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
              </div>
            ) : filteredPos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No Purchase Orders found</p>
                <Button 
                  variant="link" 
                  onClick={() => navigate("/safety/po")}
                  className="text-emerald-600"
                >
                  Upload a PO first
                </Button>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left w-12"></th>
                      <th className="px-4 py-3 text-left">PO Number</th>
                      <th className="px-4 py-3 text-left">Vendor</th>
                      <th className="px-4 py-3 text-center">Products</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-4 py-3 text-left">Order Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPos.map((po) => (
                      <tr
                        key={po.id}
                        onClick={() => handleSelectPo(po)}
                        className={`border-t cursor-pointer transition-colors ${
                          selectedPo?.id === po.id 
                            ? 'bg-emerald-50 border-emerald-200' 
                            : 'hover:bg-gray-50'
                        }`}
                        data-testid={`po-row-${po.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className={`w-4 h-4 rounded-full border-2 ${
                            selectedPo?.id === po.id 
                              ? 'border-emerald-600 bg-emerald-600' 
                              : 'border-gray-300'
                          }`}>
                            {selectedPo?.id === po.id && (
                              <div className="w-full h-full flex items-center justify-center">
                                <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono font-medium">{po.po_number}</td>
                        <td className="px-4 py-3 text-gray-600">{po.vendor_name || '-'}</td>
                        <td className="px-4 py-3 text-center">{po.product_count} items</td>
                        <td className="px-4 py-3 text-center">
                          <StatusBadge status={po.status} />
                        </td>
                        <td className="px-4 py-3 text-gray-600">{po.order_date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* QR Settings */}
            {selectedPo && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-3">Selected PO</h3>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <p className="font-mono font-bold text-lg">{selectedPo.po_number}</p>
                    <p className="text-gray-600">{selectedPo.vendor_name}</p>
                    <p className="text-sm text-gray-500">{selectedPo.product_count} products</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-3">QR Generation Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="batch-number">Batch Number</Label>
                      <Input
                        id="batch-number"
                        value={batchNumber}
                        onChange={(e) => setBatchNumber(e.target.value)}
                        placeholder="01"
                        className="mt-1"
                        data-testid="batch-number-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="qr-date">QR Code Date</Label>
                      <Input
                        id="qr-date"
                        type="date"
                        value={qrDate}
                        onChange={(e) => setQrDate(e.target.value)}
                        className="mt-1"
                        data-testid="qr-date-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Date embedded in QR code (YYYYMMDD)</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Next Button */}
            {selectedPo && products.length > 0 && (
              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={() => setStep(2)} 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading}
                  data-testid="next-to-step-2"
                >
                  {loading ? "Loading..." : "Next: Review Products"}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Review Products */}
        {step === 2 && (
          <div className="bg-white rounded-lg border shadow-sm p-6" data-testid="step-2-review">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Package className="w-5 h-5" />
                Review Products from PO #{selectedPo?.po_number}
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>Batch: {batchNumber}</span>
                <span>Date: {qrDate}</span>
              </div>
            </div>

            {/* Selection Controls */}
            <div className="mb-4 flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAll} data-testid="deselect-all-btn">
                Deselect All
              </Button>
              <span className="ml-auto text-sm text-gray-600">
                {products.filter(p => p.selected).length} of {products.length} selected
              </span>
            </div>

            {/* Products Table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left w-12"></th>
                    <th className="px-4 py-3 text-left">Product Code</th>
                    <th className="px-4 py-3 text-left">Description</th>
                    <th className="px-4 py-3 text-right">Qty</th>
                    <th className="px-4 py-3 text-left">QR Code ID (Preview)</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => (
                    <tr 
                      key={index} 
                      className={`border-t cursor-pointer hover:bg-gray-50 ${product.selected ? 'bg-emerald-50' : ''}`}
                      onClick={() => toggleProduct(index)}
                      data-testid={`product-row-${index}`}
                    >
                      <td className="px-4 py-3">
                        <Checkbox 
                          checked={product.selected} 
                          onCheckedChange={() => toggleProduct(index)}
                          data-testid={`product-checkbox-${index}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono font-medium">{product.product_code}</td>
                      <td className="px-4 py-3 text-gray-600">{product.description || '-'}</td>
                      <td className="px-4 py-3 text-right">{product.qty}</td>
                      <td className="px-4 py-3 font-mono text-sm text-emerald-600">
                        {product.product_code}-{qrDate.replace(/-/g, '')}-{batchNumber}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* QR URL Preview */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>QR URL Preview:</strong>{' '}
                <code className="bg-gray-200 px-2 py-1 rounded text-xs">
                  {settings.base_url}{products[0]?.product_code || 'PRODUCT'}-{qrDate.replace(/-/g, '')}-{batchNumber}
                </code>
              </p>
            </div>

            {/* Navigation Buttons */}
            <div className="mt-6 flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="back-to-step-1">
                Back
              </Button>
              <Button 
                onClick={handleGenerateQR} 
                disabled={loading || products.filter(p => p.selected).length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="generate-qr-btn"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <QrCode className="w-4 h-4 mr-2" />
                    Generate {products.filter(p => p.selected).length} QR Codes
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Export */}
        {step === 3 && generatedBatch && (
          <div className="bg-white rounded-lg border shadow-sm p-6" data-testid="step-3-export">
            <div className="text-center mb-6">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-green-700">QR Codes Generated Successfully!</h2>
              <p className="text-gray-600 mt-2">{generatedBatch.total_codes} QR codes created for PO #{generatedBatch.po_number}</p>
            </div>

            {/* QR Preview Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
              {generatedBatch.qr_codes.map((qr, index) => (
                <div key={index} className="border rounded-lg p-3 text-center bg-white" data-testid={`qr-preview-${index}`}>
                  <img
                    src={`${API}/qr/batches/${generatedBatch.id}/qr/${qr.qr_code_id}`}
                    alt={qr.qr_code_id}
                    className="w-full aspect-square object-contain mb-2"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <p className="font-mono text-xs truncate">{qr.qr_code_id}</p>
                  <p className="text-xs text-gray-500">Qty: {qr.qty}</p>
                </div>
              ))}
            </div>

            {/* Export Button */}
            <div className="flex flex-col items-center gap-4">
              <Button 
                onClick={() => downloadZip(generatedBatch.id, generatedBatch.po_number, generatedBatch.batch_number, generatedBatch.qr_date)}
                className="bg-emerald-600 hover:bg-emerald-700 px-8 py-3 text-lg"
                data-testid="download-zip-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                Download ZIP (QR Images + CSV)
              </Button>
              <p className="text-sm text-gray-500">
                Contains {generatedBatch.total_codes} PNG files and qr_manifest.csv for BarTender
              </p>
            </div>

            {/* Start New */}
            <div className="mt-8 pt-6 border-t text-center">
              <p className="text-gray-500 mb-3">This batch is saved in History for future downloads</p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={resetWizard} data-testid="start-new-btn">
                  Generate More QR Codes
                </Button>
                <Button variant="outline" onClick={() => navigate("/safety/history")}>
                  View History
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SafetyQRGeneration;
